import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Car, Train, Bed, UtensilsCrossed, Compass, Home, Plane, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { hebergements, restaurants, activites, filterByDistance } from '../components/venir/venirData';
import HebergementsTab from '../components/venir/HebergementsTab';
import RestaurantsTab from '../components/venir/RestaurantsTab';
import ActivitesTab from '../components/venir/ActivitesTab';
import HarasTab from '../components/venir/HarasTab';

export default function Venir() {
  const { t } = useTranslation();
  const [hebergementFilter, setHebergementFilter] = useState("all");
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [activiteFilter, setActiviteFilter] = useState("all");

  const filteredHebergements = useMemo(() => filterByDistance(hebergements, hebergementFilter), [hebergementFilter]);
  const filteredRestaurants = useMemo(() => filterByDistance(restaurants, restaurantFilter), [restaurantFilter]);
  const filteredActivites = useMemo(() => filterByDistance(activites, activiteFilter), [activiteFilter]);

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="venir-page">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-lime-50 via-white to-fuchsia-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">Nous Rejoindre</span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-zinc-900 mb-6">{t('venir.howToCome')}</h1>
            <p className="text-zinc-600 max-w-2xl mx-auto text-lg">{t('venir.heroDesc')}</p>
          </div>
        </div>
      </section>

      {/* Map & Address */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="overflow-hidden border-0 shadow-xl" data-testid="map-card">
                <div className="aspect-video lg:aspect-[16/10]">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d-0.0395!3d46.4875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48047487e5555555%3A0x1234567890abcdef!2sLa%20Briouse%2C%2086600%20Sanxay!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Domaine de la Briouse - La Briouse 86600 Sanxay" />
                </div>
              </Card>
            </div>
            <div className="space-y-6">
              <Card className="border-0 shadow-lg" data-testid="address-card">
                <CardContent className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2"><MapPin className="h-5 w-5 text-fuchsia-500" />Adresse</h3>
                  <div className="space-y-2 text-zinc-600">
                    <p className="font-semibold text-zinc-900">Domaine de la Briouse</p>
                    <p>La Briouse</p><p>86600 Sanxay</p><p>France</p>
                  </div>
                  <a href="https://www.google.com/maps/dir//Domaine+de+la+Briouse,+La+Briouse,+86600+Sanxay" target="_blank" rel="noopener noreferrer" className="mt-4 block">
                    <Button className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg" data-testid="directions-btn"><Navigation className="mr-2 h-4 w-4" />{t('venir.seeOnMap')}</Button>
                  </a>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg" data-testid="access-card">
                <CardContent className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-4">Accès</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <Car className="h-5 w-5 text-lime-500 flex-shrink-0 mt-1" />
                      <div><p className="font-medium text-zinc-900">{t('venir.byCar')}</p><p className="text-sm text-zinc-500">À 30 min de Poitiers via la D742. Parking gratuit.</p></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Train className="h-5 w-5 text-lime-500 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-zinc-900">{t('venir.byTrain')}</p>
                        <p className="text-sm text-zinc-500 mb-2"><strong>Gare de Rouillé</strong> à 8 km (depuis Poitiers)</p>
                        <div className="flex flex-wrap gap-2">
                          <a href="https://www.ter.sncf.com/nouvelle-aquitaine" target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="text-xs rounded-full"><ExternalLink className="h-3 w-3 mr-1" />SNCF TER</Button></a>
                          <a href="https://www.sncf-connect.com/" target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="text-xs rounded-full"><ExternalLink className="h-3 w-3 mr-1" />SNCF Connect</Button></a>
                          <a href="https://www.ouigo.com/" target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="text-xs rounded-full"><ExternalLink className="h-3 w-3 mr-1" />Ouigo</Button></a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Plane className="h-5 w-5 text-lime-500 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-zinc-900">{t('venir.byPlane')}</p>
                        <p className="text-sm text-zinc-500 mb-2">Aéroport de Poitiers-Biard (40 km)</p>
                        <a href="https://www.poitiers.aeroport.fr/" target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="text-xs rounded-full"><ExternalLink className="h-3 w-3 mr-1" />Aéroport de Poitiers</Button></a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 text-white">
                <CardContent className="p-6 text-center">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold mb-2">Contact</h3>
                  <a href="tel:+33749513090" className="block text-white/90 hover:text-white mb-1">07 49 51 30 90</a>
                  <Link to="/contact" className="block text-white/90 hover:text-white text-sm">{t('venir.contactBtn')} →</Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-12 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="hebergements" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 bg-transparent h-auto mb-8">
              <TabsTrigger value="hebergements" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-white shadow-sm py-3 rounded-lg"><Bed className="h-4 w-4 mr-2" />{t('venir.accommodation')}</TabsTrigger>
              <TabsTrigger value="restaurants" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-white shadow-sm py-3 rounded-lg"><UtensilsCrossed className="h-4 w-4 mr-2" />{t('venir.restaurants')}</TabsTrigger>
              <TabsTrigger value="activites" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-white shadow-sm py-3 rounded-lg"><Compass className="h-4 w-4 mr-2" />{t('venir.discover')}</TabsTrigger>
              <TabsTrigger value="haras" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-white shadow-sm py-3 rounded-lg"><Home className="h-4 w-4 mr-2" />Haras</TabsTrigger>
            </TabsList>
            <TabsContent value="hebergements"><HebergementsTab filteredHebergements={filteredHebergements} hebergementFilter={hebergementFilter} setHebergementFilter={setHebergementFilter} /></TabsContent>
            <TabsContent value="restaurants"><RestaurantsTab filteredRestaurants={filteredRestaurants} restaurantFilter={restaurantFilter} setRestaurantFilter={setRestaurantFilter} /></TabsContent>
            <TabsContent value="activites"><ActivitesTab filteredActivites={filteredActivites} activiteFilter={activiteFilter} setActiviteFilter={setActiviteFilter} /></TabsContent>
            <TabsContent value="haras"><HarasTab /></TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
